import React, { useState, useEffect, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./JobDescriptionForm.css";
import Label from "../Common/Label";
import LogoIcon from "../../assets/images/LogoNameBlack.png";
import ButtonComponent from "../Common/ButtonComponent";
import { postRequest } from "../../api/apiServices";
import apiList from "../../api/apiList";
import { useNavigate, useParams } from "react-router-dom";
import Loading from "../Compaign/UI/Loading";
import Error from "../Compaign/UI/ErrorMessage";

interface JobDescription {
  id?: number;
  logo?: string;
  name?: string;
  companyVisionAndMission?: string;
  jobDescription?: string;
  employmentType?: string;
}

const JobListing: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [jobDescription, setJobDescription] = useState<JobDescription | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const { token } = useParams();
  const navigate = useNavigate();

  const fetchJobFunction = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const res = await postRequest(apiList.jobData, { jobToken: token });
      if (res?.data?.success) {
        setJobDescription(res.data.data || null);
      } else {
        setError(`Failed to load job data`);
      }
    } catch (error: any) {
      setError("API Error:" + error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchJobFunction();
    } else {
      setIsLoading(false);
    }
  }, [token]);

  const handleApplyClick = (): void => {
    if (token) {
      navigate(`/easy-apply/${token}`);
    } else {
      navigate("/");
    }
  };

  const companyMissionText = jobDescription?.companyVisionAndMission?.trim();
  const heroSummary = useMemo(() => {
    if (!companyMissionText) return null;
    return companyMissionText.length > 220
      ? `${companyMissionText.slice(0, 220).trim()}...`
      : companyMissionText;
  }, [companyMissionText]);

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return <Error error={error} onRetry={() => window.location.reload()} />;
  }

  return (
    <section className="job-listing">
      <span className="blur-circle blur-circle--one" />
      <span className="blur-circle blur-circle--two" />
      <div className="container">
        <div className="job-card">
          <div className="job-card__header">
            <div className="job-card__identity">
              <div className="logo-wrapper">
                <img
                  src={jobDescription?.logo || LogoIcon}
                  alt="Company Logo"
                />
              </div>
              <div>
                <Label
                  children={jobDescription?.name || "Job Title Not Available"}
                  className="job-titles"
                />
                <div className="job-card__meta-row">
                  <span className="meta-pill">
                    {jobDescription?.employmentType || "Flexible opportunity"}
                  </span>
                  <span className="meta-pill meta-pill--light">
                    {jobDescription?.name
                      ? `Join the ${jobDescription.name} team`
                      : "Grow your career with us"}
                  </span>
                </div>
              </div>
            </div>
            <ButtonComponent
              className="btn apply-btn apply-btn--primary"
              onClick={handleApplyClick}
              children="I'm interested"
            />
          </div>

          {/* {heroSummary && <p className="job-card__summary">{heroSummary}</p>} */}

          {/* <div className="job-card__highlights">
            <div className="highlight-block">
              <span className="highlight-label">Focus</span>
              <p className="highlight-value">
                {companyMissionText || "We build meaningful careers and teams."}
              </p>
            </div>
            <div className="highlight-block">
              <span className="highlight-label">Role fit</span>
              <p className="highlight-value">
                {jobDescription?.employmentType
                  ? `${jobDescription.employmentType} engagement`
                  : "Tailored to your strengths"}
              </p>
            </div>
          </div> */}
        </div>

        {companyMissionText && (
          <div className="content-panel">
            <div className="content-panel__header">
              <Label className="section-title" children="About the company" />
              <span className="section-caption">
                Discover what drives our culture and growth plans.
              </span>
            </div>
            <div className="section-content">
              <Label children={companyMissionText} className="mb-0" />
            </div>
          </div>
        )}

        {jobDescription?.jobDescription && (
          <div className="content-panel">
            <div className="content-panel__header">
              <Label className="section-title" children="Role overview" />
              <span className="section-caption">
                Responsibilities, impact areas, and expectations.
              </span>
            </div>
            <div className="section-content rich-text">
              <div
                {...({
                  dangerouslySetInnerHTML: {
                    __html: jobDescription.jobDescription || "",
                  },
                } as any)}
              />
            </div>
          </div>
        )}

        <div className="content-panel content-panel--cta">
          <div>
            <h3 className="cta-title">
              Ready to grow with {jobDescription?.name || "our team"}?
            </h3>
            <p className="cta-copy">
              Share your profile and we will be in touch to explore how this
              role aligns with your aspirations.
            </p>
          </div>
          <ButtonComponent
            className="btn apply-btn apply-btn--contrast"
            onClick={handleApplyClick}
            children="Start application"
          />
        </div>
      </div>
    </section>
  );
};

export default JobListing;
